import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  addCategory,
  updateCategory,
  type Category,
} from '../api/resourceService';
import { useTranslation } from 'react-i18next';

export default function CategoryForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Category;
  onSaved?: (c: Category) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  
  type FormValues = {
    name_en: string;
    name_es: string;
  };

  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm<FormValues>({
    defaultValues: initial
      ? {
        name_en: initial.name.en,
        name_es: initial.name.es,
      }
      : {
        name_en: '',
        name_es: '',
      },
  });

  const watchedNameEn = watch('name_en');
  const watchedNameEs = watch('name_es');

  useEffect(() => {
    if (initial) {
      reset({ name_en: initial.name.en, name_es: initial.name.es });
    }
  }, [initial, reset]);

  const onSubmit = handleSubmit(async (values: FormValues) => {
    const payload = { name: { en: values.name_en, es: values.name_es } };
    const saved = initial
      ? await updateCategory({ id: initial.id, ...payload })
      : await addCategory(payload);
    onSaved?.(saved);
  });

  return (
    <div className="w-full max-w-3xl mx-auto max-h-[40vh] flex flex-col">
      {/* Header del formulario - STICKY ARRIBA */}
      <div className="sticky top-0 bg-[var(--surface)] z-10 p-6 pb-4 border-b border-[var(--border)]">
        <h3 className="text-3xl font-bold text-[var(--text-default)] mb-2">
          {initial ? t('categoryForm.editTitle') : t('categoryForm.title')}
        </h3>
        <p className="text-[var(--text-muted)]">
          {initial ? t('categoryForm.editSubtitle') : t('categoryForm.subtitle')}
        </p>
      </div>

      {/* Formulario */}
      <div className="p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Campos de nombre en inglés y español */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('categoryForm.nameEn')}
              </label>
              <div className="relative">
                <input
                  {...register('name_en', { 
                    required: t('categoryForm.validation.nameEnRequired'),
                    maxLength: { value: 25, message: t('categoryForm.validation.maxLength', { count: 25 }) }
                  })}
                  placeholder={t('categoryForm.nameEnPlaceholder')}
                  maxLength={25}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                  {watchedNameEn?.length || 0}/25
                </div>
              </div>
              {errors.name_en && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.name_en.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('categoryForm.nameEs')}
              </label>
              <div className="relative">
                <input
                  {...register('name_es', { 
                    required: t('categoryForm.validation.nameEsRequired'),
                    maxLength: { value: 25, message: t('categoryForm.validation.maxLength', { count: 25 }) }
                  })}
                  placeholder={t('categoryForm.nameEsPlaceholder')}
                  maxLength={25}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                  {watchedNameEs?.length || 0}/25
                </div>
              </div>
              {errors.name_es && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.name_es.message}</p>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] transition-colors duration-200 font-medium"
            >
              {t('categoryForm.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[var(--text-default)] text-[var(--surface)] rounded-lg hover:bg-[var(--text-muted)] transition-colors duration-200 font-medium"
            >
              {initial ? t('categoryForm.actions.update') : t('categoryForm.actions.create')}
            </button>
          </div>
        </form>
      </div>

      {/* Footer con botones - STICKY ABAJO */}
      <div className="sticky bottom-0 bg-[var(--surface)] z-10 p-6 pt-4 border-t border-[var(--border)]">
        {/* Los botones ya están dentro del formulario arriba */}
      </div>
    </div>
  );
}
